import ButtonRow from 'lib/src/components/button/ButtonRow';
import LinkButton from '@components/button/LinkButton';
import Table from 'lib/src/components/table/Table';
import { AdminUser } from 'src/types/shared/AdminUser';
import { Dispatch, SetStateAction } from 'react';
import { userRoleValues } from '../../../types/shared/User';
import ActionButton from 'lib/src/components/button/ActionButton';
import useRoles from 'lib/src/hooks/useRoles';
import { findRoleMatch } from 'lib/src/utils/generic';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';
import { siteNames } from 'lib/src/shared/enums/site';

const AdminUsersTable: React.FC<AdminUsersTableProps> = ({
    adminUsers,
    isFetching,
    setAdminUserToEdit,
    setAdminUserToEditPassword,
    setAdminUserToDelete,
    setAdminUserToUpgrade,
    setAdminUserToDowngrade,
}) => {
    const roles = useRoles();
    const isSuperAdmin = roles.includes(userRoles.superAdmin);
    const columns = [
        {
            key: 1,
            heading: 'Name',
            getValue: (row: AdminUser) => `${row.firstName} ${row.lastName}`,
            getSort: (a: AdminUser, b: AdminUser) => {
                try {
                    return a.firstName.localeCompare(b.firstName);
                } catch (e) {
                    return 0;
                }
            },
            searchable: true,
        },
        {
            key: 2,
            heading: 'Email',
            getValue: (row: AdminUser) => row.email,
            searchable: true,
        },
        {
            key: 3,
            heading: 'Roles',
            getValue: (row: AdminUser) =>
                row.roles
                    .filter(r => !!userRoleValues[r])
                    .map(r => userRoleValues[r])
                    .join(', '),
        },
        {
            key: 4,
            heading: 'Site Permissions',
            getValue: (row: AdminUser) => siteNames[row.site],
        },
        {
            key: 5,
            heading: '',
            getValue: (row: AdminUser) => {
                const isRoleMatch = isSuperAdmin || findRoleMatch(roles, row.roles);
                return (
                    <ButtonRow alignment="top">
                        <LinkButton color="green" icon="search" href={`/admins/${row.id}`}>
                            View
                        </LinkButton>
                        {isRoleMatch && (
                            <>
                                <ActionButton
                                    color="yellow"
                                    icon="pencil"
                                    onClick={() => setAdminUserToEdit(row.id)}
                                >
                                    Edit
                                </ActionButton>
                                <ActionButton
                                    color="grey"
                                    icon="pencil"
                                    onClick={() => setAdminUserToEditPassword(row.id)}
                                >
                                    Edit Password
                                </ActionButton>
                                <ActionButton
                                    color="red"
                                    icon="times"
                                    onClick={() => setAdminUserToDelete(row.id)}
                                >
                                    Remove
                                </ActionButton>
                            </>
                        )}
                        {isSuperAdmin && (
                            <>
                                {!row.roles.includes(userRoles.superAdmin) ? (
                                    <ActionButton
                                        color="blue"
                                        icon="crown"
                                        onClick={() => setAdminUserToUpgrade(row.id)}
                                    >
                                        Upgrade to Super Admin
                                    </ActionButton>
                                ) : (
                                    <ActionButton
                                        color="blue"
                                        icon="users-cog"
                                        onClick={() => setAdminUserToDowngrade(row.id)}
                                    >
                                        Downgrade to Admin
                                    </ActionButton>
                                )}
                            </>
                        )}
                    </ButtonRow>
                );
            },
        },
    ];

    return <Table columns={columns} rows={Object.values(adminUsers)} isLoading={isFetching} />;
};

interface AdminUsersTableProps {
    adminUsers: Record<number, AdminUser>;
    isFetching: boolean;
    setAdminUserToEdit: Dispatch<SetStateAction<number | null>>;
    setAdminUserToEditPassword: Dispatch<SetStateAction<number | null>>;
    setAdminUserToDelete: Dispatch<SetStateAction<number | null>>;
    setAdminUserToUpgrade: Dispatch<SetStateAction<number | null>>;
    setAdminUserToDowngrade: Dispatch<SetStateAction<number | null>>;
}

export default AdminUsersTable;
